import * as React from "react";
import { observer, inject } from "mobx-react";
import { toJS } from "mobx";
import BootstrapTable from "react-bootstrap-table-next";
import { Link } from "react-router-dom";

import FlagCustomerDialog from "./FlagCustomerDialog";
import { CustomerLink } from "../common/CustomerLink";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { successMessage, errorMessage } from "../common/Toast";
import { CovertAndFormatCentsToDollars } from "src/common/Methods/FileUtil";
import InvolvementComponent from "./InvolvementComponent";

import { SessionStore } from "../../stores/SessionStore";
import { CustomerStore } from "../../stores/CustomerStore";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import { HorseForSale } from "src/model/HorseForSale";
import { ExtractFriendlyName, Business, Contact } from "../../model/Customer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard, faShoppingCart, faFlag, faBan } from "@fortawesome/free-solid-svg-icons";

export interface State {
    flagCustomerDialogVisible: boolean;
    checkoutHorsesForSale: HorseForSale[];
}

export interface Props {
    customerStore?: CustomerStore;
    customerEndpoints?: CustomerEndpoints;
}

@inject("customerStore")
@inject("sessionStore")
@inject("customerEndpoints")
@observer
export default class Overview extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            flagCustomerDialogVisible: false,
            checkoutHorsesForSale: []
        };
    }

    onUnflag = async () => {
        const goAhead = await SimpleConfirmation("Are you sure that you'd like to unflag this customer?");
        if (!goAhead) {
            return;
        }

        try {
            await this.props.customerStore!.updateFields([{ field: "flaggedNotes", value: null }]);
            successMessage("This customer has been unflagged.");
        } catch (error) {
            errorMessage(error);
        }
    };

    render() {
        const { customerStore } = this.props;
        if (!customerStore) {
            throw new Error(" ** No Customer Store **");
        }

        const customer = customerStore.customer;
        if (!customer) {
            return {};
        }

        const involvement = (customer as Contact).involvement;

        return (
            <div className="card shadow">
                <div className="card-header">Overview</div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-12">
                            <form action="">
                                {customer.flaggedNotes && (
                                    <div className="alert alert-warning">
                                        <div className="row">
                                            <div className="col-9">{customer.flaggedNotes}</div>
                                            <div className="col-3 text-right">
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        type="button"
                                                        onClick={() => this.setState({ flagCustomerDialogVisible: true })}
                                                    >
                                                        <FontAwesomeIcon icon={faFlag} className="mr-2" /> Edit Flag
                                                    </button>
                                                    <button className="btn btn-danger btn-sm" type="button" onClick={this.onUnflag}>
                                                        <FontAwesomeIcon icon={faBan} className="mr-2" /> Unflag
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!customer.flaggedNotes && (
                                    <div className="row mr-0">
                                        <div className="col-12 pr-0 text-right">
                                            <button
                                                className="btn btn-primary btn-sm"
                                                type="button"
                                                data-toggle="button"
                                                aria-pressed="false"
                                                onClick={() => this.setState({ flagCustomerDialogVisible: true })}
                                            >
                                                <FontAwesomeIcon icon={faFlag} className="mr-2" /> Flag Customer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">Basic Customer Details</div>
                                <div className="card-body">
                                    <div className="form-group row my-0">
                                        <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                            Name:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className="form-control-plaintext"
                                                readOnly={true}
                                                value={ExtractFriendlyName(customer)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row my-0">
                                        <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                            Address:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className="form-control-plaintext"
                                                readOnly={true}
                                                value={
                                                    customer.address
                                                        ? (customer.address.address ? customer.address.address : "") +
                                                          ", " +
                                                          (customer.address.city ? customer.address.city : "") +
                                                          ", " +
                                                          (customer.address.province ? customer.address.province.name : "") +
                                                          " " +
                                                          (customer.address.postalCode ? customer.address.postalCode : "") +
                                                          " " +
                                                          (customer.address.country ? customer.address.country.name : "")
                                                        : ""
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row my-0">
                                        <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                            Phone #:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className="form-control-plaintext"
                                                readOnly={true}
                                                value={customer.primaryPhone ? customer.primaryPhone : ""}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row my-0">
                                        <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                            Email:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className="form-control-plaintext"
                                                readOnly={true}
                                                value={customer.email ? customer.email : ""}
                                            />
                                        </div>
                                    </div>

                                    {customer.type === "BUSINESS" && (
                                        <div className="form-group row my-0">
                                            <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                                Contacts:
                                            </label>
                                            <div className="col-sm-10 py-0 mt-2">
                                                <ul className="">
                                                    {(customer as Business).contacts.map((contact, index) => (
                                                        <li key={index}>
                                                            <CustomerLink customer={contact.contact} targetBlank={true}>
                                                                ({contact.title})
                                                            </CustomerLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    {customer.type === "CONTACT" && (
                                        <div className="form-group row my-0">
                                            <label htmlFor="staticEmail" className="col-sm-2 col-form-label text-secondary">
                                                Related Businesses:
                                            </label>
                                            <div className="col-sm-10 py-0 mt-2">
                                                <ul className="">
                                                    {(customer as Contact).contactFor.map((contact, index) => (
                                                        <li key={index}>
                                                            <CustomerLink customer={contact.business} targetBlank={true}>
                                                                ({contact.title})
                                                            </CustomerLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-group row my-0">
                                        <label htmlFor="account-balance" className="col-sm-2 col-form-label text-secondary">
                                            Account Balance:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className="form-control-plaintext"
                                                readOnly={true}
                                                value={"$" + CovertAndFormatCentsToDollars(customer.account.balance)}
                                                name="account-balance"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group row my-0">
                                        <label htmlFor="account-balance" className="col-sm-2 col-form-label text-secondary">
                                            Past Due Amount:
                                        </label>
                                        <div className="col-sm-10 py-0">
                                            <input
                                                type="text"
                                                className={"form-control-plaintext " + (customer.account.pastDueBalance > 0 ? "text-danger" : "")}
                                                readOnly={true}
                                                value={"$" + CovertAndFormatCentsToDollars(customer.account.pastDueBalance)}
                                                name="account-balance"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card  mt-5">
                        <div className="card-header">Involvement</div>
                        <div className="card-body">
                            <InvolvementComponent
                                keyField="currentInvolvement"
                                defaultStateFilter={"REJECTED"}
                                selectAvailable={true}
                                currentInvolvement={true}
                                defaultTab="winningBidder"
                                involvements={involvement}
                                search={false}
                                customer={customer}
                            />
                        </div>
                    </div>
                </div>
                <FlagCustomerDialog
                    show={this.state.flagCustomerDialogVisible}
                    onToggle={() => this.setState({ flagCustomerDialogVisible: !this.state.flagCustomerDialogVisible })}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/Overview.tsx